// eslint-disable-next-line boundaries/element-types
import { useStoreGroups } from '@/entities/contact-group'
// eslint-disable-next-line boundaries/element-types
import { OutgoingCallsHandlingConditions, WEB_CLIENT_TERMINAL_ID } from '@/entities/user-terminal'

import { PreferenceKey, Preferences } from '..'

type PreferenceMigration = {
  from: number | number[]
  to: number | number[]
  migrate: (preferences: Preferences) => Preferences | Promise<Preferences>
}

const migrations: PreferenceMigration[] = [
  {
    from: 7,
    to: 8,
    migrate: (preferences) => ({
      ...preferences,
      [PreferenceKey.OUTGOING_CALLS_HANDLING_CONDITION]: OutgoingCallsHandlingConditions.DEFAULT,
      [PreferenceKey.DEFAULT_TERMINAL_ID]: WEB_CLIENT_TERMINAL_ID,
      [PreferenceKey.VERSION]: 8,
    }),
  },
  {
    from: [7, 8],
    to: 9,
    migrate: async (preferences) => {
      const newPreferences: Preferences = {
        ...preferences,
        [PreferenceKey.VERSION]: 9,
      }

      const visibleGroupIds = preferences[PreferenceKey.VISIBLE_CONTACT_GROUPS]
      if (!Array.isArray(visibleGroupIds) || visibleGroupIds?.length === 0) {
        newPreferences[PreferenceKey.HIDDEN_CONTACT_GROUPS] = []
        delete newPreferences[PreferenceKey.VISIBLE_CONTACT_GROUPS]
        return newPreferences
      }

      try {
        const groupsStore = useStoreGroups()
        await groupsStore.load()
        const allGroupIds = groupsStore.groups.map((g) => g.guid)
        if (allGroupIds.length > 0) {
          const hiddenGroupIds = allGroupIds.filter((id: string) => !visibleGroupIds.includes(id))
          newPreferences[PreferenceKey.HIDDEN_CONTACT_GROUPS] = hiddenGroupIds
          delete newPreferences[PreferenceKey.VISIBLE_CONTACT_GROUPS]
        }
      } catch (e) {
        console.error(e)
      }

      return newPreferences
    },
  },
  {
    from: 9,
    to: 10,
    migrate: (preferences) => ({
      ...preferences,
      [PreferenceKey.CHAT_FOLDERS]: [],
      [PreferenceKey.VERSION]: 10,
    }),
  },
]

const isEqualPreferences = (left: Preferences, right: Preferences) => {
  const leftEntries = Object.entries(left)
  const rightEntries = Object.entries(right)

  if (leftEntries.length !== rightEntries.length) {
    return false
  }

  return leftEntries.every(([key, value]) => right[key as PreferenceKey] === value)
}

export const applyMigrations = async (inputPreferences: Preferences, targetVersion: number): Promise<{ preferences: Preferences, hasChanges: boolean }> => {
  const from = inputPreferences[PreferenceKey.VERSION] as number

  const targetMigration = migrations.find(migration => {
    if (typeof migration.from === 'number') {
      return migration.from === from
    }

    return migration.from.includes(from)
  })
  if (!targetMigration) {
    const migratedPreferences = {
      ...inputPreferences,
      [PreferenceKey.VERSION]: targetVersion,
    }
    return {
      preferences: migratedPreferences,
      hasChanges: !isEqualPreferences(inputPreferences, migratedPreferences),
    }
  }

  let currentVersion = from
  let currentPreferences = { ...inputPreferences }

  for (const migration of migrations) {
    const match = (typeof migration.from === 'number' ? migration.from === currentVersion : migration.from.includes(currentVersion)) &&
    (typeof migration.to === 'number' ? migration.to === targetVersion : migration.to.includes(targetVersion))
    if (match && migration.to === targetVersion) {
      currentPreferences = await migration.migrate(currentPreferences)
      currentVersion = migration.to
    }
  }

  const migratedPreferences = {
    ...currentPreferences,
    [PreferenceKey.VERSION]: targetVersion,
  }

  return {
    preferences: migratedPreferences,
    hasChanges: !isEqualPreferences(inputPreferences, migratedPreferences),
  }
}
